/**
 * Codec resolver for mapping file extensions to appropriate video and audio codecs.
 * Based on xconfig codec mappings for FFmpeg compatibility.
 *
 * @module codec-resolver
 */

// ============================================================================
// VIDEO CODEC MAPPINGS
// ============================================================================

const VIDEO_CODEC_MAP: Readonly<Record<string, string>> = {
    // VP9 for WebM
    'WEBM': 'VP9',

    // H264 for most common formats
    'MP4': 'H264',
    'MOV': 'H264',
    '3G2': 'H264',
    '3GP': 'H264',
    'M4V': 'H264',
    'MKV': 'H264',

    // FLV codec
    'FLV': 'FLV',
    'SWF': 'FLV',

    // RealMedia
    'RMVB': 'RV10',
    'RM': 'RV10',

    // MPEG1
    'MPG': 'MPEG1',
    'MPEG': 'MPEG1',

    // MPEG2
    'M2V': 'MPEG2',
    'MTS': 'MPEG2',
    'M2TS': 'MPEG2',
    'MXF': 'MPEG2',
    'TS': 'MPEG2',
    'VOB': 'MPEG2',
    'WTV': 'MPEG2',

    // MPEG4 for AVI
    'AVI': 'MPEG4',

    // WMV
    'WMV': 'WMV2',

    // HEVC/H265
    'HEVC': 'H265',
};

// ============================================================================
// AUDIO CODEC MAPPINGS
// ============================================================================

const AUDIO_CODEC_MAP: Readonly<Record<string, string>> = {
    // AAC for common video/audio formats
    'AAC': 'AAC',
    'M4A': 'AAC',
    'M4R': 'AAC',
    'M4V': 'AAC',
    'MOV': 'AAC',
    'MP4': 'AAC',
    'MKV': 'AAC',
    'F4V': 'AAC',
    'HEVC': 'AAC',
    'AV1': 'AAC',

    // AC3
    'AC3': 'AC3',
    'AVCHD': 'AC3',
    'RM': 'AC3',
    'RMVB': 'AC3',

    // PCM for lossless audio
    'AIF': 'PCM_S16BE',
    'AIFF': 'PCM_S16BE',
    'CAF': 'PCM_S16BE',
    'WAV': 'PCM_S16LE',
    'W64': 'PCM_S16LE',
    'VOC': 'PCM_S16LE',

    // AMR for 3GP
    '3G2': 'AMR',
    '3GP': 'AMR',
    'AMR': 'AMR',

    // DTS
    'DTS': 'DCA',

    // FLAC
    'FLAC': 'FLAC',

    // MP2 for MPEG formats
    'M2TS': 'MP2',
    'MP2': 'MP2',
    'MPEG': 'MP2',
    'MPEG2': 'MP2',
    'MPG': 'MP2',
    'MTS': 'MP2',
    'TS': 'MP2',
    'VOB': 'MP2',

    // MP3
    'AVI': 'MP3',
    'DIVX': 'MP3',
    'FLV': 'MP3',
    'MJPEG': 'MP3',
    'MP3': 'MP3',
    'SWF': 'MP3',
    'XVID': 'MP3',

    // Vorbis for OGG
    'OGA': 'VORBIS',
    'OGG': 'VORBIS',
    'OGV': 'VORBIS',

    // Opus for WebM
    'OPUS': 'OPUS',
    'WEBM': 'OPUS',

    // Other formats
    'RA': 'REAL_144',
    'SPX': 'SPEEX',
    'TTA': 'TTA',
    'WV': 'WAVPACK',
    'ASF': 'WMAV2',
    'WMA': 'WMAV2',
    'WMV': 'WMA',
    'MXF': 'WAV',
};

// ============================================================================
// DEFAULT CODECS
// ============================================================================

/** Default video codec when extension is not found in the map */
export const DEFAULT_VIDEO_CODEC = 'H264';

/** Default audio codec when extension is not found in the map */
export const DEFAULT_AUDIO_CODEC = 'AAC';

// ============================================================================
// RESOLVER FUNCTIONS
// ============================================================================

/**
 * Normalize extension for lookup (uppercase, no leading dot)
 */
function normalizeExtension(extension: string): string {
    return extension.toUpperCase().replace(/^\./, '');
}

/**
 * Get the appropriate video codec for a given output extension
 * @param extension - The output file extension (e.g., 'MP4', 'WEBM', '.mp4')
 * @returns The video codec to use (e.g., 'H264', 'VP9')
 */
export function getVideoCodecForExtension(extension: string): string {
    const normalized = normalizeExtension(extension);
    return VIDEO_CODEC_MAP[normalized] || DEFAULT_VIDEO_CODEC;
}

/**
 * Get the appropriate audio codec for a given output extension
 * @param extension - The output file extension (e.g., 'MP4', 'MP3', '.mp3')
 * @returns The audio codec to use (e.g., 'AAC', 'MP3')
 */
export function getAudioCodecForExtension(extension: string): string {
    const normalized = normalizeExtension(extension);
    return AUDIO_CODEC_MAP[normalized] || DEFAULT_AUDIO_CODEC;
}

/**
 * Get both video and audio codecs for a video container format
 * @param extension - The output video container extension
 * @returns Object with videoCodec and audioCodec
 */
export function getCodecsForVideoContainer(extension: string): { videoCodec: string; audioCodec: string } {
    return {
        videoCodec: getVideoCodecForExtension(extension),
        audioCodec: getAudioCodecForExtension(extension),
    };
}

/**
 * Check if a video codec is supported for a given extension
 * @param extension - The file extension
 * @param codec - The codec to check
 */
export function isVideoCodecSupported(extension: string, codec: string): boolean {
    const expectedCodec = getVideoCodecForExtension(extension);
    return expectedCodec === codec;
}

/**
 * Check if an audio codec is supported for a given extension
 * @param extension - The file extension
 * @param codec - The codec to check
 */
export function isAudioCodecSupported(extension: string, codec: string): boolean {
    const expectedCodec = getAudioCodecForExtension(extension);
    return expectedCodec === codec;
}
